import { useRealTimeTypedQuery } from "components/WebSocket/hooks";
import { PIN_STATUSES } from "models";
import { ORDER_BY, ORDER_BY_LOCATION, WARRANTY_TIME_PERIODS } from "modules/WarrantyDashboard/hooks/useGetFilterOptions";
import { useGetWarrantyDashboardFilters } from "modules/WarrantyDashboard/hooks/useGetWarrantyDashboardFilters";
import { useWarrantyDashboardListeners } from "modules/WarrantyDashboard/listeners";
import { WarrantyQueryKeys } from "modules/WarrantyDashboard/queryKeys";
import { ApiInstance } from "utils/Api";

import type { Pin } from "models";
import type { WarrantyDashboardFilters } from "modules/WarrantyDashboard/queryKeys";
import type { PaginatedItems } from "utils/paginatedItems";

const orderByCompare = (a: Pin, b: Pin, orderBy: ORDER_BY) => {
  switch (orderBy) {
    case ORDER_BY.ClaimAscending:
    case ORDER_BY.ClaimDescending:
      return orderBy === ORDER_BY.ClaimAscending ? a.claim_nr.localeCompare(b.claim_nr) : b.claim_nr.localeCompare(a.claim_nr);

    case ORDER_BY.PinUpdatedOnAscending:
    case ORDER_BY.PinUpdatedOnDescending:
      return orderBy === ORDER_BY.PinUpdatedOnAscending ? a.updated_on.localeCompare(b.updated_on) : b.updated_on.localeCompare(a.updated_on);

    case ORDER_BY.AppointmentDateAscending:
    case ORDER_BY.AppointmentDateDescending: {
      const appDateA = a.appointment?.time_car_app ?? "";
      const appDateB = b.appointment?.time_car_app ?? "";
      return orderBy === ORDER_BY.AppointmentDateAscending ? appDateA.localeCompare(appDateB) : appDateB.localeCompare(appDateA);
    }

    default:
      return 0;
  }
};

const orderByLocationCompare = (a: string, b: string, orderBy: ORDER_BY_LOCATION | null) => {
  if (!orderBy) return 0;

  return orderBy === ORDER_BY_LOCATION.LocationAscending ? a.localeCompare(b) : b.localeCompare(a);
};

const sortPaginatedWarrantyPins = (paginatedPins: PaginatedItems<Pin>, filters: WarrantyDashboardFilters) => {
  const orderBy = filters.order_by ?? filters.claim_order_by ?? ORDER_BY.AppointmentDateAscending;

  const sortedItems = paginatedPins.items?.toSorted(
    (a, b) => orderByLocationCompare(a.location_name ?? "", b.location_name ?? "", filters.order_by_location) || orderByCompare(a, b, orderBy)
  );

  return { ...paginatedPins, items: sortedItems };
};

const getPinStatuses = (deleted: boolean, pinStatuses: PIN_STATUSES[] | null) => {
  if (deleted) return [PIN_STATUSES.Deleted];
  if (pinStatuses?.length) return [...pinStatuses];

  return null;
};

export const prepareWarrantyQueryPayload = (filters: WarrantyDashboardFilters) => {
  const {
    claim_order_by,
    order_by,
    appointment_statuses,
    time_period: _timePeriod,
    appointment_time_period,
    pin_status_id,
    pin_status_refresh_timestamp: _pinStatusRefreshTimestamp,
    deleted,
    ...rest
  } = filters;
  const activeAppointmentStatuses = appointment_statuses.filter(({ isActive }) => isActive).map(({ identifier }) => identifier);

  return {
    ...rest,
    order_by: claim_order_by || order_by,
    appointment_statuses: activeAppointmentStatuses,
    pin_status_id: getPinStatuses(deleted, pin_status_id),
    include_future: appointment_time_period !== WARRANTY_TIME_PERIODS.CustomTimePeriod
  };
};

export const useWarrantyQuery = () => {
  const { filters } = useGetWarrantyDashboardFilters();
  const listeners = useWarrantyDashboardListeners();

  const hasDealerLocationSelected = filters.location_ids.length > 0 || filters.dealer_ids.length > 0;
  const hasDateRangeSelected =
    filters.pin_date_from !== null && filters.pin_date_to !== null && filters.appointment_date_from !== null && filters.appointment_date_to !== null;
  const isEnabled = hasDealerLocationSelected && hasDateRangeSelected;
  const params = prepareWarrantyQueryPayload(filters);

  const { data, isFetching, refetch } = useRealTimeTypedQuery({
    queryKey: WarrantyQueryKeys.listWarranties,
    queryFn: async ({ baseUrl, endpoint }) =>
      params.appointment_statuses.length > 0
        ? (await ApiInstance.post(endpoint, params, baseUrl)).data
        : new Promise(r =>
            r({
              items: [],
              nb_pages: 0,
              nb_items: 0
            })
          ),
    select: (paginatedPins: PaginatedItems<Pin>) => sortPaginatedWarrantyPins(paginatedPins, filters),
    enabled: false,
    refetchOnDependencies: [isEnabled, filters],
    listeners
  });

  return {
    warrantyPins: data?.items || [],
    nbItems: data?.nb_items || 0,
    nbPages: data?.nb_pages || 1,
    isFetching,
    refetch
  };
};
